/**
 * The permission vocabulary the UI gates on.
 *
 * The key names are unchanged, so the 35 components that import this file keep
 * working — but the **values are now scope strings, not numeric ids**. That
 * swap is the point:
 *
 *  - the legacy numbers were `proto_privileges.id`, hard-coded here and in the
 *    database, and they had already collided across environments once (an
 *    out-of-band "in-app help" feature claimed 55-57 in one deployment, forcing
 *    scheduling to be renumbered 55/56 -> 58/59 by a repair migration);
 *  - twenty-one of the sixty legacy privileges were never checked anywhere. They
 *    were grantable, they appeared in the admin UI and in reports, and they
 *    gated nothing. Several are folded into a real scope below; the ones that
 *    were never authorization at all are marked ALWAYS.
 *
 * `ALWAYS` means "not a permission" — a UI preference or a nominal right the
 * server does not gate. The gate returns true for it rather than pretending a
 * check happened.
 */

export const ALWAYS = '@always';

/**
 * "Any member of staff" — true for everyone except a patient or a patient's
 * contact. Not a permission the server knows: it stands in for a legacy
 * privilege that gated only UI, where main's default matrix gives it to every
 * staff group and to neither Patients nor Contacts.
 */
export const STAFF = '@staff';

export const PRIVILEGES = Object.freeze({
  // --- not authorization -------------------------------------------------
  // Authentication is the identity provider's business; the SPA only ever
  // holds a token that already proves it.
  LOGIN: ALWAYS,
  // The help viewer: every group holds it on main. Not a right.
  HELP_VIEW: ALWAYS,
  // The billable-time timer on Vitals and Notes. Main's product matrix grants
  // application_timer (49) to every staff group and to neither Patients nor
  // Contacts, and the pages auto-start it when it is held — so ALWAYS would
  // start a billable clock on a patient's own vitals screen. The server never
  // gated it, so it is not a scope; STAFF reproduces the default. What it
  // cannot reproduce is an organization revoking it from one staff group.
  APPLICATION_TIMER: STAFF,

  // --- patients & people -------------------------------------------------
  EDIT_PATIENT_PROFILE: 'patients:write',
  EDIT_PATIENT_CONTACT: 'contacts:write',
  CREATE_ACCOUNTS: 'users:write',
  MANAGE_ACCOUNT_CREDENTIALS: 'users:credentials',
  ASSIGN_STAFF_TO_PATIENTS: 'groups:admin',
  ASSIGN_NON_PATIENTS_TO_GROUPS: 'groups:admin',

  // Entering a patient's chart is viewing it: the signed-in clinician stays
  // the actor, and the API checks the care relationship itself. Formerly
  // IMPERSONATE_PATIENT.
  VIEW_PATIENT_CHART: 'patients:view_chart',
  // Not a legacy privilege: main's ChartAccess lets any member of staff reach
  // any patient in the organization's Patients group. The port makes that one
  // revocable grant (decision D1), held by default by every staff group.
  PATIENTS_ROSTER: 'patients:roster',

  // --- documents ---------------------------------------------------------
  UPLOAD_DOCUMENTS: 'documents:write',
  VIEW_CAREGIVER_DOCUMENTS: 'caregiver_documents:read',

  // --- notes -------------------------------------------------------------
  VIEW_NOTES: 'notes:read',
  WRITE_NOTES: 'notes:write',

  // --- medications -------------------------------------------------------
  // One scope per holder set in main's 2026-08-31 matrix. Pre-pour and witness
  // are their own scopes because their holders differ from administer's: a
  // physician holds discontinue but not prepour, a GCM prepour but not
  // administer, a home health aide only witness.
  MEDICATION_VIEW: 'medications:read',
  MEDICATION_ADD_UNORDERED: 'medications:add_unordered',
  MEDICATION_ADD_ORDERED: 'medications:write',
  MEDICATION_IMPORT_FROM_DOCUMENTS: 'medications:import',
  MEDICATION_EDIT_UNORDERED: 'medications:write',
  MEDICATION_EDIT_ORDERED: 'medications:write',
  MEDICATION_HOLD: 'medications:administer',
  MEDICATION_DISCONTINUE: 'medications:administer',
  MEDICATION_ADMINISTER: 'medications:administer',
  MEDICATION_PREPOUR: 'medications:prepour',
  MEDICATION_WITNESS: 'medications:witness',

  // --- diagnoses, procedures, allergies, vitals --------------------------
  DIAGNOSIS_VIEW: 'diagnoses:read',
  DIAGNOSIS_ADD: 'diagnoses:add',
  DIAGNOSIS_EDIT_AND_DELETE: 'diagnoses:write',
  PROCEDURE_VIEW: 'procedures:read',
  PROCEDURE_ADD_UNORDERED: 'procedures:add_unordered',
  PROCEDURE_ADD_ORDERED: 'procedures:write',
  PROCEDURE_IMPORT_FROM_DOCUMENTS: 'procedures:import',
  PROCEDURE_DISCONTINUE_OR_CANCEL: 'procedures:discontinue',
  ALLERGY_ADD: 'allergies:add',
  ALLERGY_EDIT_AND_DELETE: 'allergies:write',
  VITALS_VIEW: 'vitals:read',
  VITALS_ADD_MANUAL: 'vitals:write',

  // --- orders ------------------------------------------------------------
  ORDER_VIEW: 'orders:read',
  ORDER_WRITE: 'orders:write',
  // Also requires a verified NPI on the signer — the API enforces that second
  // gate; hiding the button is only the first.
  ORDER_SIGN: 'orders:sign',

  // --- directives --------------------------------------------------------
  DIRECTIVE_READ: 'directives:read',
  DIRECTIVE_UPLOAD_MODIFY: 'directives:write',
  DIRECTIVE_APPROVE_REAUTHORIZE: 'directives:sign',
  DIRECTIVE_POLICY_MANAGE: 'directives:admin',
  // Scope, orthogonal to verb: read, write and sign each mean "for my assigned
  // patients"; this widens whichever of them the caller holds to every patient
  // in the organization. Granted by default to Administrators only. Break-glass
  // lends directives:read for emergency viewing but deliberately does NOT lend
  // this (main #329): an emergency read stays on the person's assigned
  // patients. See src/utils/directivesScope.js.
  DIRECTIVE_ALL_PATIENTS: 'directives:all_patients',
  // A patient submitting their own directive. The API pairs it with an
  // ownership check. Deliberately not DIRECTIVE_UPLOAD_MODIFY: that also
  // unlocks add-codes, confirm and revoke, which would let one patient revoke
  // another's pending directive.
  DIRECTIVE_CREATE_OWN: 'directives:create_own',

  // --- devices & billing -------------------------------------------------
  METER_DEVICE_VIEW: 'devices:read',
  METER_DEVICE_ADD: 'devices:add',
  METER_DEVICE_REMOVE: 'devices:remove',
  METER_CPT_ASSIGNMENTS_VIEW: 'cpt_assignments:read',
  METER_CPT_ASSIGNMENTS_ADD: 'cpt_assignments:write',
  METER_CPT_ASSIGNMENTS_REMOVE: 'cpt_assignments:write',
  VIEW_USE_BILLING_PAGE: 'billing:read',

  // --- scheduling --------------------------------------------------------
  // Object-scoped in the legacy model: it only counted when granted through a
  // group owned by the patient being viewed. A bearer token cannot carry "for
  // these 37 patients", so the API pairs this scope with a care-relationship
  // check instead.
  PATIENT_SCHEDULING_ADD: 'scheduling:write',
  SCHEDULE_ADMINISTER: 'scheduling:admin',

  // --- messaging ---------------------------------------------------------
  SEND_INTERNAL_MESSAGES: 'messages:write',
  // Texting or calling a patient. Its own scope because main withholds it
  // from Patients, who all hold messages:write, and enforces it on every text
  // and call that reaches a patient.
  COMMUNICATE_WITH_PATIENTS: 'patients:communicate',
  COMMUNICATE_WITH_NON_PATIENTS: 'messages:write',

  // --- administration ----------------------------------------------------
  ADJUST_PERMISSIONS: 'privileges:admin',
  VIEW_AUDITS: 'audit:read',
  // The Users and Status page (/myOrg).
  MY_ORG_VIEW: 'users:org_view',
  // Port-new (Trello 0LGomfTd): taking a person's sign-in away and giving it
  // back are each their own right, held by Administrators by default.
  DEACTIVATE_USERS: 'users:deactivate',
  REACTIVATE_USERS: 'users:reactivate',
  // Generating and downloading the privileges audit report. Its own right:
  // adjusting permissions or viewing audits no longer qualifies.
  GENERATE_PRIVILEGES: 'privileges:report',
  // Editing global help articles is cross-tenant, so the API requires platform
  // operator status on top of the scope.
  HELP_MANAGE_PLATFORM: 'help:admin',
  HELP_MANAGE_ORG: 'help:admin',

  // --- platform administration (Trello nRhCnuT0) ---------------------------
  // Platform-only: held by an operator of the platform tenant through its
  // Platform Administrators group, and by nobody in a customer organization.
  PLATFORM_ADMIN: 'platform:admin',
  PLATFORM_TENANTS: 'platform:tenants',
});

/**
 * Guard clause helper, unchanged in shape:
 *   if (!canPrivilege(store, PRIVILEGES.ORDER_SIGN)) return;
 */
export function canPrivilege(store, permission) {
  return !!store && store.effectiveGranted(permission);
}

export default PRIVILEGES;
